/**
 * Navigation History Utility
 * Tracks where users came from to enable smart back button behavior
 * 
 * EXTENDED in Batch 1 to support:
 * - Profile navigation tracking
 * - More granular source tracking for all detail pages
 */

// ─── Basic Navigation Source ─────────────────────────────────────────────────

// Store the referrer page before navigating
export const setNavigationSource = (source) => {
  sessionStorage.setItem('navigationSource', source);
};

// Get the referrer page
export const getNavigationSource = () => {
  return sessionStorage.getItem('navigationSource');
};

// Clear the referrer
export const clearNavigationSource = () => {
  sessionStorage.removeItem('navigationSource');
};

// Check if user came from a specific source
export const cameFrom = (source) => {
  return getNavigationSource() === source;
};

// ─── Extended Profile Tracking (Batch 1 Addition) ────────────────────────────

/**
 * Set navigation source with profile context
 * Used when clicking posts from profile pages
 * @param {string} source - The source page type
 * @param {string|number} profileId - The profile ID (for returning to correct profile)
 */
export const setNavigationSourceWithProfile = (source, profileId) => {
  sessionStorage.setItem('navigationSource', source);
  if (profileId) {
    sessionStorage.setItem('navigationSourceProfileId', String(profileId));
  }
};

/**
 * Get profile ID associated with navigation source
 * @returns {string|null} - The profile ID or null
 */
export const getNavigationSourceProfileId = () => {
  return sessionStorage.getItem('navigationSourceProfileId');
};

/**
 * Clear profile-related navigation data
 */
export const clearNavigationSourceProfile = () => {
  sessionStorage.removeItem('navigationSourceProfileId');
};

/**
 * Clear all navigation data (source + profile)
 */
export const clearAllNavigationData = () => {
  clearNavigationSource();
  clearNavigationSourceProfile();
};

// ─── Navigation Source Constants ─────────────────────────────────────────────

export const NAVIGATION_SOURCES = {
  HOMEPAGE_CALENDAR: 'homepage-calendar',
  HOMEPAGE_UPDATES: 'homepage-updates',
  NEWS_EVENTS: 'news-events',
  NEWS_ARTICLES: 'news-articles',
  OPPORTUNITIES: 'opportunities',
  PROFILE: 'profile',              // User's own profile
  PUBLIC_PROFILE: 'public-profile', // Someone else's profile
  FIND: 'find'
};

// ─── Helper Functions ─────────────────────────────────────────────────────────

/**
 * Get the back navigation path based on current source
 * @returns {string|null} - Path to navigate back to, or null for default back
 */
export const getBackPath = () => {
  const source = getNavigationSource();
  const profileId = getNavigationSourceProfileId();

  switch (source) {
    case NAVIGATION_SOURCES.HOMEPAGE_CALENDAR:
    case NAVIGATION_SOURCES.HOMEPAGE_UPDATES:
      return '/';
    
    case NAVIGATION_SOURCES.NEWS_EVENTS:
      return '/news?tab=events';
    
    case NAVIGATION_SOURCES.NEWS_ARTICLES:
      return '/news?tab=articles';
    
    case NAVIGATION_SOURCES.OPPORTUNITIES:
      return '/opportunities';
    
    case NAVIGATION_SOURCES.PROFILE:
      return '/profile';
    
    case NAVIGATION_SOURCES.PUBLIC_PROFILE:
      return profileId ? `/users/${profileId}` : null;
    
    case NAVIGATION_SOURCES.FIND:
      return '/find';
    
    default:
      return null; // Use browser's default back behavior
  }
};

/**
 * Navigate back based on source, with fallback
 * @param {object} navigate - React Router navigate function
 * @param {string} fallbackPath - Where to go if no source is set
 */
export const navigateBack = (navigate, fallbackPath = '/') => {
  const backPath = getBackPath();
  
  if (backPath) {
    navigate(backPath);
    clearAllNavigationData();
  } else {
    // Use browser back or fallback
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate(fallbackPath);
    }
  }
};
